import { useFrame, useThree } from '@react-three/fiber'
import React, { useEffect } from 'react'
import { useClickStore } from '../store/clickStore'
import { useShallow } from 'zustand/react/shallow'
import TWEEN from 'tween'
import * as THREE from 'three'

const DEFAULT_CAMERA_SETTINGS = {
    camPos: { x: 0, y: 1500, z: 2000 },
    lookAt: { x: -50, y: -250, z: -163.69 },
};

const reusableVector = new THREE.Vector3();

export default function CameraControl({ controls }) {
    const { camera } = useThree();

    const { zoomPosition, zoomToExtent, setZoomToExtent } = useClickStore(
        useShallow((state) => ({
            zoomPosition: state.zoomPosition,
            zoomToExtent: state.zoomToExtent,
            setZoomToExtent: state.setZoomToExtent,
        }))
    );

    useEffect(() => {
        if (zoomToExtent) {
            zoomToDefault(controls, camera);
            setZoomToExtent(false);
        }
    }, [zoomToExtent]);

    useEffect(() => {
        if (zoomPosition) {
            // First, look at the target point
            createTween(controls.current.target, zoomPosition, 800);
            
            // Then, after a short delay, move closer to the point
            setTimeout(() => {
                const newCameraPosition = calculateOptimalCameraPosition(camera.position, zoomPosition);
                createTween(camera.position, newCameraPosition, 1200);
            }, 400);
        }
    }, [zoomPosition]);

    // Add TWEEN.update() to the render loop
    useFrame(() => {
        TWEEN.update();
    });

    return null;
}

function calculateOptimalCameraPosition(currentPosition, targetPosition) {
    // Calculate the direction from camera to target
    const direction = new THREE.Vector3()
        .subVectors(targetPosition, currentPosition)
        .normalize();
    
    // Calculate distance from current position to target
    const distance = currentPosition.distanceTo(targetPosition);
    
    // Define optimal zoom distance (adjust this value as needed)
    const optimalDistance = Math.min(distance * 0.3, 500); // 30% of current distance, max 500 units
    
    // Calculate new camera position
    const newPosition = new THREE.Vector3()
        .copy(targetPosition)
        .add(direction.multiplyScalar(-optimalDistance));
    
    // Ensure camera doesn't go below ground level
    newPosition.y = Math.max(newPosition.y, targetPosition.y + 50);
    
    return newPosition;
}

function zoomToDefault(controls, camera) {
    const { camPos, lookAt } = DEFAULT_CAMERA_SETTINGS;

    createTween(controls.current.target, lookAt, 2100);
    createTween(camera.position, camPos, 2000);
}

function createTween(target, to, duration) {
    new TWEEN.Tween(target)
        .to(
            {
                x: to.x,
                y: to.y,
                z: to.z,
            },
            duration
        )
        .easing(TWEEN.Easing.Quadratic.Out)
        .start();
}

